import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { StoreState } from "../redux/reducers";
import {
  fetchLeaveRequests,
  addLeaveRequest,
  editLeaveRequest,
  deleteLeaveRequestById,
  editLeaveStatus,
} from "../redux/actions/leaveRequestAction";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import { LeaveRequest } from "../types/LeaveRequest";
import LeaveRequestForm from "../components/LeaveRequestForm";
import LeaveRequestList from "../components/LeaveRequestList";
import { useToast } from "../components/toast/ToastProvider";
import { AuthUserType } from "../redux/actions/authUserActions";
import { fetchLeavesAction } from "../redux/actions/leaveTransactionAction";
import LeaveRequestFilter from "../components/LeaveRequestFilter";

const initialLeaveRequests: LeaveRequest[] = [];

const LeaveRequestPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const leaveRequests = useSelector((state: StoreState) => state.leaveRequest);
  const authUser = useSelector<StoreState, AuthUserType>(
    (state) => state.authUser,
  );

  const [leaveRequest, setLeaveRequest] =
    useState<LeaveRequest[]>(initialLeaveRequests);
  const [selected, setSelected] = useState<LeaveRequest | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filteredRequest, setFilteredRequest] = useState<LeaveRequest[]>([]);
  const [filters, setFilters] = useState({
    employeeId: "",
    year: "",
    status: "New",
  });

  useEffect(() => {
    setLoading(true);
    dispatch(fetchLeaveRequests()).finally(() => setLoading(false));
    // setFilteredRequest(leaveRequests)
  }, [dispatch]);

  const handleSave = async (req: LeaveRequest) => {
    setLoading(true);
    try {
      if (selected) {
        await dispatch(editLeaveRequest(req.id, req));

        toast("Leave request updated successfully");
      } else {
        await dispatch(addLeaveRequest(req));

        toast("Leave request added successfully");
      }
      await dispatch(fetchLeaveRequests());
      setSelected(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving leave request");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this leave request?")) {
      setLoading(true);
      try {
        await dispatch(deleteLeaveRequestById(id));
        toast("Leave request deleted successfully");
        await dispatch(fetchLeaveRequests());
      } catch (error) {
        toast("Error deleting leave request");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (item: LeaveRequest) => {
    setSelected(item);
    setShowModal(true);
  };

  const handleApprove = async (req: LeaveRequest) => {
    if (req.status === "Approved") return;

    const updated = {
      ...req,
      status: "Approved",
      approvedBy: authUser?.name || "Unknown",
    };

    setLoading(true);
    try {
      await dispatch(editLeaveStatus(req.id, updated));
      toast("Leave request approved successfully");
      await dispatch(fetchLeaveRequests());
      await dispatch(fetchLeavesAction());
    } catch (error) {
      toast("Error approving leave request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (req: LeaveRequest) => {
    const updated = {
      ...req,
      status: "Cancelled",
      approvedBy: authUser?.name || "Unknown",
    };

    setLoading(true);
    try {
      await dispatch(editLeaveStatus(req.id, updated));
      toast("Leave request approved successfully");
      await dispatch(fetchLeaveRequests());
      await dispatch(fetchLeavesAction());
    } catch (error) {
      toast("Error approving leave request");
    } finally {
      setLoading(false);
    }
  };

  //Filter Logic

  useEffect(() => {
    setFilteredRequest(leaveRequests);
  }, [leaveRequests]);

  const applyFilters = (newFilters: typeof filters) => {
    setFilters(newFilters);

    const filtered = leaveRequests.filter((req) => {
      const matchesEmployee =
        !newFilters.employeeId || req.employeeId === newFilters.employeeId;

      const matchesYear =
        !newFilters.year || String(req.year) === String(newFilters.year);

      const matchesStatus =
        !newFilters.status ||
        req.status?.toLowerCase() === newFilters.status.toLowerCase();

      return matchesEmployee && matchesYear && matchesStatus;
    });

    setFilteredRequest(filtered);
  };
  const handleReset = () => {
    setFilters({ employeeId: "", year: "", status: "New" });
    setFilteredRequest(leaveRequests);
  };

  return (
    <Main>
      {/* <PageContainer title="Leave Request Management" buttonText={loading ? "Loading..." : "ADD"} onButtonClick={() => setShowModal(true)}> */}

      <PageContainer title="Leave Request Management">
        <Modal
          visible={showModal}
          size="lg"
          onClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          headerText={selected ? "Update Leave Request" : "Add Leave Request"}
        >
          <LeaveRequestForm
            onSave={handleSave}
            initialData={selected}
            clearAfterSubmit={() => setSelected(null)}
            employeeId={authUser?.employeeId || ""}
          />
        </Modal>

        <CollapseCard title="Leave Request Filter">
          <LeaveRequestFilter
            filters={filters}
            onFilterChange={applyFilters}
            onReset={handleReset}
          />
        </CollapseCard>

        <CollapseCard title="Leave Requests">
          <LeaveRequestList
            leaveRequests={filteredRequest}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onApprove={handleApprove}
            onCancel={handleCancel}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default LeaveRequestPage;
